import { Accordion, Box, Divider, Flex, Stack, createStyles, useMantineTheme } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';
import { useState } from 'react';

import { UnstyledButton } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { Drawer } from '~/domains/design-system/components/Drawer/Drawer';
import dt from '~/domains/contest/domains/manage/domains/common/testing.const';

import type { BaseInterativeField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import type { APIField } from '../../types/main';
import { flattenArray } from '../../../context/utils/flattenArray';
import type { FieldQuery } from '../../../context/types/store';

type APIAccordionItem = {
  key: string;
  fields: APIField[];
  label: string;
  sublabel: string;
  params?: {
    isOpennedByDefault?: boolean;
  };
};

type AccordionItem = Omit<APIAccordionItem, 'fields'> & {
  fields: string[];
};

export type APIAccordionGroupType = BaseInterativeField & {
  type: 'AccordionGroup';
  data: APIAccordionItem[];
};

export type AccordionGroupType = Omit<APIAccordionGroupType, 'data'> & {
  fields: string[];
  data: AccordionItem[];
};

function TableModalTitle({ label }: { label?: string }) {
  return label ? (
    <Title ta="center" variant="headline-large">
      {label}
    </Title>
  ) : null;
}

const useStyles = createStyles(() => ({
  accordionContent: {
    padding: `0`,
  },
}));

function AccordionElement({
  field,
  sectionKey,
  path,
  FieldSwitch,
}: StaticFieldProps<AccordionGroupType>) {
  const theme = useMantineTheme();
  const [isOpen, setIsOpen] = useState(false);
  const { classes } = useStyles();

  const defaultValue = field.data.find((item) => item.params?.isOpennedByDefault)?.key;

  return (
    <div>
      <Divider />
      <Flex justify="center" /* eslint-disable-line local-rules/use-theme-tokens */ pt={14} pb={theme.other.spacing._6}>
        <UnstyledButton onClick={() => setIsOpen(true)} data-test-id={dt.action.editSettings}>
          <Text variant="label-small" c={theme.colors.blue[9]}>
            Edit settings
          </Text>
        </UnstyledButton>
      </Flex>
      <Drawer
        title={<TableModalTitle label={field?.label} />}
        onClose={() => setIsOpen(false)}
        onToggle={setIsOpen}
        open={isOpen}
      >
        <div>
          <Accordion
            w="100%"
            classNames={{ content: classes.accordionContent }}
            defaultValue={defaultValue}
          >
            {field.data.map((item) => (
              <Accordion.Item value={item.key} key={item.key}>
                <Accordion.Control>
                  <Stack spacing={theme.other.spacing._2}>
                    <Text variant="headline-medium">{item.label}</Text>
                    <Text variant="title-medium" c={theme.colors.gray2[4]}>
                      {item.sublabel}
                    </Text>
                  </Stack>
                </Accordion.Control>
                <Accordion.Panel>
                  {item.fields.map((key) => (
                    <Box sx={{ borderTop: `1px solid ${theme.colors.gray2[1]}` }} key={key}>
                      <FieldSwitch
                        key={key}
                        fieldKey={key}
                        sectionKey={sectionKey}
                        path={[...path, item.key]}
                      />
                    </Box>
                  ))}
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
      </Drawer>
    </div>
  );
}

const Descriptor: FieldDescriptor<APIAccordionGroupType, AccordionGroupType> = {
  FieldComponent: AccordionElement,
  name: 'AccordionGroup',
  parseFieldResponse: ({ field, fieldKey, parse }) => {
    const data = field.data.reduce((acc, child) => {
      acc[child.key] = child.fields
        .map((childField) => parse(childField, `${fieldKey}.${child.key}.`))
        .filter(Boolean);

      return acc;
    }, {});

    const normalizedField: FieldQuery = {
      key: fieldKey,
      type: field.type,
      data: {
        ...(field as unknown as AccordionGroupType),
        data: field.data.map((child) => ({
          ...child,
          fields: data[child.key].map((f) => f[0].key),
        })),
      },
    };

    return [normalizedField, flattenArray(Object.values(data))];
  },
  createValidationSchema: (field, create) => {
    const schemaObj = field.data.reduce(
      (acc, item) => {
        const schemaFieldsObj: Record<string, ZodTypeAny> = {};
        item.fields.forEach((fieldKey) => {
          const result = create(fieldKey);
          if (result) {
            if ('field' in result) {
              const [key, schema] = result.field;
              schemaFieldsObj[key] = schema;
            } else {
              result.fields.forEach(([key, schema]) => {
                schemaFieldsObj[key] = schema;
              });
            }
          }
        });
        acc[item.key] = z.object(schemaFieldsObj);
        if (Object.values(schemaFieldsObj).every((schema) => schema.isOptional())) {
          acc[item.key] = acc[item.key].optional();
        }

        return acc;
      },
      {} as Record<string, ZodTypeAny>
    );

    let schema: ZodTypeAny = z.object(schemaObj);

    if (Object.values(schemaObj).every((s) => s.isOptional())) {
      schema = schema.optional();
    }

    if (field.validation?.isOptional) {
      schema = schema.optional();
    }

    return schema;
  },
};

export default Descriptor;
